<template>
  <section class="py-16 px-4 md:px-8 lg:px-16 bg-gray-50">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      
      <!-- About Section -->
      <div>
        <h2 class="text-3xl md:text-4xl font-bold mb-6 text-gray-800">
          {{ themeStore.theme.AboutUs.title }}
        </h2>
        <p class="text-gray-600 mb-6 leading-relaxed">
          {{ themeStore.theme.AboutUs.description }}
        </p>
        
        <!-- Stats -->
        <div class="flex gap-8 mb-6">
          <div>
            <span class="text-4xl font-bold text-[#FDB813]">
              <AnimatedNumber :target="260" :duration="1500" />
            </span>
            <p class="text-gray-600">Free Courses</p>
          </div>
          <div>
            <span class="text-4xl font-bold text-[#FDB813]">
              <AnimatedNumber :target="152" :duration="1500" />
            </span>
            <p class="text-gray-600">Paid Courses</p>
          </div>
        </div>
      </div>

      <!-- Video Thumbnail -->
      <div 
        class="relative group w-full h-auto rounded-lg shadow-lg overflow-hidden cursor-pointer" 
        @click="openVideoPopup"
      >
        <img
          :src="carousel1"
          alt="Video Thumbnail"
          class="w-full h-full object-cover"
        />
        <div
          class="absolute inset-0 flex items-center justify-center bg-opacity-40 rounded-lg transition-opacity duration-300 group-hover:opacity-100"
        >
          <div class="bg-white p-4 rounded-full flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
            <Play :size="32" class="text-[#FDB813]" />
          </div>
        </div>
      </div>
    </div>

    <!-- Video Popup Modal -->
    <transition name="fade">
      <div
        v-if="showVideoPopup"
        class="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
        @click.self="closeVideoPopup"
      >
        <div class="relative w-full max-w-3xl bg-black rounded-lg overflow-hidden" @click.stop>
          
          <!-- Close Button -->
          <button
            @click="closeVideoPopup"
            class="absolute top-4 right-4 z-50 bg-black/50 p-2 rounded-full text-white hover:bg-black/70 focus:outline-none"
            aria-label="Close video"
          >
            <X class="h-6 w-6" />
          </button>

          <!-- Video -->
          <video
            ref="popupVideoRef"
            :src="video"
            class="w-full max-h-[80vh] object-contain"
            controls
            autoplay
            @ended="closeVideoPopup"
          ></video>
        </div>
      </div>
    </transition>
  </section>
</template>

<script setup>
import video from '../../assets/video.mp4'; // ✅ Video file import
import { ref, onMounted, onUnmounted } from 'vue';
import { Play, X } from 'lucide-vue-next';
import AnimatedNumber from './AnimatedNumber.vue'; 
import { useThemeStore } from '../../stores/themeStore';
import carousel1 from '../../assets/carousel1.jpg'
const themeStore = useThemeStore();
const showVideoPopup = ref(false);
const popupVideoRef = ref(null);

// Open video popup
const openVideoPopup = () => {
  showVideoPopup.value = true;
};

// Close video popup
const closeVideoPopup = () => {
  showVideoPopup.value = false;
  if (popupVideoRef.value) {
    popupVideoRef.value.pause();
    popupVideoRef.value.currentTime = 0; // Reset video to start
  }
};

// Handle ESC key to close popup
const handleKeydown = (event) => {
  if (event.key === 'Escape' && showVideoPopup.value) {
    closeVideoPopup();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
/* Transition for the video popup */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
